import { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Paper, Fade, Button } from '@mui/material';
import Deck from '../game/Deck';
import GameStart from '../game/GameStart';
import useTeamColors from '../hooks/useTeamColors';
import { ANIMATION_DURATIONS } from '../config/animationConfig.js';

const SUIT_CODES = { oros: 1, copas: 2, espadas: 3, bastos: 4 };
const SUIT_NAMES = { oros: 'Oros', copas: 'Copas', espadas: 'Espadas', bastos: 'Bastos' };
const POSITION_TEXT = {
    same: 'el mismo jugador',
    right: 'el jugador a su derecha',
    opposite: 'el jugador de enfrente',
    left: 'el jugador a su izquierda'
};

function cardImagePath(suit, value) {
    return `assets/images/deck/${SUIT_CODES[suit]}${String(value).padStart(2, '0')}.png`;
}

/**
 * GameStartSequence Component
 *
 * Reveals who deals first: a random player "cuts" the deck, a card is
 * revealed, and the starting dealer is determined from its suit.
 *
 * This is a plain DOM/MUI overlay (same concept as the online equivalent,
 * CutTheDeckModal.jsx) rather than its own PixiJS canvas. It used to size a
 * separate Application from window.innerWidth/innerHeight once at mount,
 * which went stale the moment useLandscapeOrientation's native lock
 * physically rotated the screen right after this component appeared
 * (menus are free-orientation, so entering the game can trigger a real
 * rotation) - the canvas kept its pre-rotation size, breaking the layout.
 * A DOM overlay just reflows normally with the rest of the page.
 *
 * Dealer selection itself is unchanged: still the offline Deck/GameStart
 * classes, still decided client-side.
 *
 * This should only be shown when both teams have 0 points (start of the game)
 */
const GameStartSequence = ({ players, onComplete }) => {
    const { teamColors } = useTeamColors();
    const [revealed, setRevealed] = useState(false);

    const result = useMemo(() => {
        const deck = new Deck();
        deck.shuffle();
        const cuttingPlayerIndex = Math.floor(Math.random() * 4);
        const revealedCard = deck.cards[Math.floor(Math.random() * deck.cards.length)];
        const gameStart = new GameStart(players);
        const dealerIndex = gameStart.getDealerIndex(cuttingPlayerIndex, revealedCard.suit);
        const position = gameStart.getPositionDescription(cuttingPlayerIndex, dealerIndex);

        return {
            cuttingPlayer: players[cuttingPlayerIndex],
            revealedCard,
            startingDealer: players[dealerIndex],
            dealerIndex,
            position
        };
    }, [players]);

    useEffect(() => {
        const timer = setTimeout(() => setRevealed(true), ANIMATION_DURATIONS.SEQUENCE_WAIT);
        return () => clearTimeout(timer);
    }, []);

    const handleContinue = () => {
        if (onComplete) {
            onComplete(result);
        }
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Fade in timeout={800}>
                <Paper
                    elevation={10}
                    sx={{
                        p: { xs: 3, sm: 4 },
                        maxWidth: { xs: '90%', sm: 450 },
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: 3,
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        ¿Quién reparte primero?
                    </Typography>

                    {!revealed ? (
                        <Typography variant="body1" sx={{ my: 3, color: 'text.secondary' }}>
                            {result.cuttingPlayer.avatar} {result.cuttingPlayer.name} está cortando el mazo...
                        </Typography>
                    ) : (
                        <Fade in timeout={500}>
                            <Box>
                                <Typography variant="body1" sx={{ my: 1 }}>
                                    {result.cuttingPlayer.avatar} {result.cuttingPlayer.name} cortó el mazo. Carta revelada:{' '}
                                    <strong>{result.revealedCard.value} de {SUIT_NAMES[result.revealedCard.suit]}</strong>
                                </Typography>
                                <Box
                                    component="img"
                                    src={cardImagePath(result.revealedCard.suit, result.revealedCard.value)}
                                    alt={`${result.revealedCard.value} de ${SUIT_NAMES[result.revealedCard.suit]}`}
                                    sx={{ width: 90, my: 1, borderRadius: 1, boxShadow: 2 }}
                                />
                                <Box
                                    sx={{
                                        p: 2,
                                        my: 2,
                                        backgroundColor: teamColors.team1.light,
                                        borderRadius: 2,
                                        border: `2px solid ${teamColors.team1.main}`
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: teamColors.team1.main }}>
                                        {result.startingDealer.avatar} {result.startingDealer.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Será el primer repartidor
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        ({POSITION_TEXT[result.position]})
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    onClick={handleContinue}
                                    sx={{
                                        mt: 1,
                                        py: 1.5,
                                        fontSize: '1.1rem',
                                        fontWeight: 'bold',
                                        borderRadius: 2,
                                        background: `linear-gradient(135deg, ${teamColors.team1.main} 0%, #764ba2 100%)`,
                                        '&:hover': {
                                            background: `linear-gradient(135deg, #764ba2 0%, ${teamColors.team1.main} 100%)`
                                        }
                                    }}
                                >
                                    Continuar al juego
                                </Button>
                            </Box>
                        </Fade>
                    )}
                </Paper>
            </Fade>
        </Box>
    );
};

export default GameStartSequence;
